// ============================================================================
// MÃ“DULO 6: CLIENT SCHEMA & VALIDATION (JURÃDICO AVANÃ‡ADO - V2.0)
// ARQUIVO: src/6_Client_Schema.js
// OBJETIVO: Estrutura de dados profunda para qualificação completa (CPC/2015).
// ATUALIZAÃ‡ÃƒO: Inclusão de dados completos de Representantes/Tutores/Síndicos.
// ============================================================================

import { fetchClientDoc } from "../services/FileHubCacheRepository";
import { FIREBASE_CONFIG } from "./Config";
import { REGISTER_PROVIDERS } from "./ClientConstants";
import { escapeHtml } from "../utils/escapeHtml";

/** Mapa de correcao de mojibakes para sanitizar dados do Firestore */
const MOJIBAKE_MAP = {
  "nÃ£o": "não",
  "Ã©": "é",
  "Ã¡": "á",
  "Ã£": "ã",
  "Ã¢": "â",
  Ãª: "ê",
  "Ã³": "ó",
  Ãµ: "õ",
  "Ã¼": "ü",
  "Ã§": "ç",
  Ãº: "ú",
  "Ã­": "í",
  "Ã´": "ô",
};

/**
 * ENUMERAÃ‡ÃƒO DE TIPOS DE PESSOA
 */
export const CLIENT_TYPES = {
  PHYSICAL: "Pessoa Física",
  LEGAL: "Pessoa Jurídica",
  ESTATE: "Espólio (Falecido)",
  CONDOMINIUM: "Condomínio",
  MINOR: "Menor/Incapaz",
  ANIMAL: "Animal Não-Humano",
  IMPRISONED: "Pessoa Privada de Liberdade",
  NASCITURO: "Nascituro (Gestação)",
  BANKRUPTCY: "Massa Falida",
  PUBLIC_ENTITY: "Pessoa Jurídica de Direito Público",
};

// --- CONSTANTES DE INTEGRIDADE (INTERNAS DO SCHEMA) ---
export const ANCHOR_ROLES = {
  TUTOR: "TUTOR",
  RESPONSABLE: "RESPONSABLE",
};

export const LOCATION_STATUS = {
  VERIFIED: "Endereço Verificado",
  UNKNOWN: "Local Incerto e Não Sabido (LINS)",
  IMPRISONED_STATUS: "Recolhido em Unidade Prisional",
  ABROAD: "Residente no Exterior",
  HOMELESS: "Pessoa em Situação de Rua",
  LEGAL_DOMICILE: "Domicílio Necessário / Funcional",
};

// Enum interno para validação (não confundir com a lista de UI)
export const MARITAL_REGIMES_ENUM = {
  COMUNHAO_PARCIAL: "COMUNHAO_PARCIAL",
  COMUNHAO_UNIVERSAL: "COMUNHAO_UNIVERSAL",
  SEPARACAO_TOTAL: "SEPARACAO_TOTAL",
  PARTICIPACAO_FINAL_AQUESTOS: "PARTICIPACAO_FINAL_AQUESTOS",
};

// --- ESTRUTURAS AUXILIARES ---
const AddressSchema = {
  street: "",
  number: "",
  complement: "",
  neighborhood: "",
  city: "",
  state: "PR",
  zipCode: "",
};

const RepresentativeSchema = {
  name: "",
  socialName: "",
  pronoun: "",
  grammaticalGender: "",
  gender: "",
  birthDate: "",
  approximateAgeMonths: "",
  nationality: "brasileiro(a)",
  naturalness: "",
  civilStatus: "casado(a)",
  maritalRegime: "",
  spouseName: "",
  spouseCPF: "",
  profession: "",
  rg: "",
  orgaoEmissor: "",
  cpf: "",
  hasProfessionalRegister: false,
  registerType: "",
  registerUf: "",
  professionalRegister: "",
  address: { ...AddressSchema },
  contact: { phone: "", email: "" },
};

const AnchorSchema = {
  id: "",
  name: "",
  cpf: "",
  role: ANCHOR_ROLES.TUTOR,
};

// --- SCHEMA PRINCIPAL (V7.2 - ATUALIZADO) ---
export const ClientSchema = {
  id: null,
  type: CLIENT_TYPES.PHYSICAL,
  active: true,
  createdAt: null,
  healthCondition: [],
  grammaticalGender: "",
  isOfficeProfile: false,
  onboardingStatus: "APPROVED",
  isDeleted: false,
  auditLog: [],

  anchor: { ...AnchorSchema },
  isFullDependent: false,
  livesWithAnchor: false,

  contact: {
    email: "",
    phonePrimary: "",
    emergencyContact: { phone: "", name: "", relation: "" },
    isDigitalNative: true,
  },

  qualification: {
    // 1. PESSOA FÃSICA (V7.2)
    physical: {
      name: "",
      socialName: "",
      pronoun: "ELE",
      genderIdentity: "",
      grammaticalGender: "M",
      birthDate: "",
      motherName: "",
      fatherName: "",
      approximateAgeMonths: "",
      nationality: "brasileiro(a)",
      naturalness: "", // NOVO: Naturalidade
      civilStatus: "solteiro(a)",
      maritalRegime: "", // NOVO: Regime de Bens
      spouseName: "", // NOVO: Cônjuge
      spouseCPF: "",
      profession: "",
      hasProfessionalRegister: false,
      registerType: "", // Ex: OAB/PR
      registerUf: "",
      professionalRegister: "", // Número
      isPPE: false,
      ppeDescription: "",
      // Documento de identidade — RG tradicional (número + órgão emissor
      // estadual) OU Carteira de Identidade Nacional (CIN, modelo gov.br
      // unificado desde 2026, cujo número de "Registro Geral" É o próprio
      // CPF — não existe RG numérico separado nesse modelo).
      identityDocType: "RG", // "RG" | "CIN"
      rg: "",
      orgaoEmissor: "",
      cinOrgaoExpedidor: "", // ex.: "Instituto de Identificação do Paraná"
      cinLocalEmissao: "", // ex.: "Curitiba/PR"
      cinDataEmissao: "",
      cinDataValidade: "",
      cpf: "",
      electoral: { voterId: "", zone: "", section: "" },
      // Outros Documentos (24/08/2026) — independentes do tipo de carteira
      // de identidade (RG ou CIN): qualquer pessoa pode ter CNH, PIS/PASEP,
      // NIS, NIT ou Carteira de Trabalho, não são exclusivos de nenhuma
      // delas. Raramente preenchidos e raramente citados na qualificação
      // padrão — servem de repositório pronto para quando um tipo
      // específico de processo (trabalhista, trânsito, benefício
      // previdenciário) precisar desses dados. Seção própria no formulário,
      // recolhida por padrão — decisão do titular, 24/08/2026.
      cnh: "",
      cnhCategoria: "",
      pisPasep: "",
      nis: "",
      nit: "",
      carteiraTrabalho: { numero: "", serie: "", uf: "" },
    },

    // 2. PESSOA JURÃDICA
    legal: {
      companyName: "",
      tradeName: "",
      cnpj: "",
      stateRegistry: "",
      representativeRole: "Sócio-Administrador",
      nire: "",
      constitutionDate: "",
      companyType: "",
      representative: { ...RepresentativeSchema },
    },

    // 3. MENOR / INCAPAZ
    minor: {
      name: "",
      nationality: "brasileiro(a)",
      rg: "",
      cpf: "",
      representativeType: "Assistido(a)",
      representativeParent: "Genitor(a)",
      guardianshipProcessNumber: "",
      guardianshipCourt: "",
      representative: { ...RepresentativeSchema },
    },

    // 4. ANIMAL (V7.2)
    animal: {
      name: "",
      species: "Canina",
      breed: "SRD",
      scientificName: "",
      biologicalSex: "",
      reproductiveStatus: "",
      coat: { color: "", pattern: "", texture: "" },
      distinctiveMarks: [],
      microchip: "",
      registries: [],
      rga: "",
      chipId: "",
      gender: "",
      pronoun: "O",
      biological: { estimatedAge: "" },
      originOfGuardianship: "",
      health: { veterinarianName: "" },
      responsible: { ...RepresentativeSchema }, // NOVO: Responsible
    },

    // 5. PRESIDIÃRIO (V7.2)
    imprisoned: {
      name: "",
      nationality: "brasileiro(a)",
      civilStatus: "solteiro(a)",
      profession: "",
      rg: "",
      cpf: "",
      matriculaPrisional: "", // NOVO: INFOPEN
      rji: "",
      prisonUnit: "",
      cityUnit: "",
      executionRegime: "",
      penalExecutionCourt: "",
      hasProxy: true,
      proxyRelation: "Procurador(a)",
      proxy: { ...RepresentativeSchema },
    },

    // Demais tipos mantidos com estrutura padrão
    estate: {
      deceasedName: "",
      nationality: "brasileiro(a)",
      profession: "",
      cpf: "",
      dateOfDeath: "",
      inventorier: { ...RepresentativeSchema },
    },
    condominium: {
      name: "",
      cnpj: "",
      syndic: { ...RepresentativeSchema },
      syndicElectionDate: "",
      syndicMandateExpiration: "",
    },
    unborn: {
      mothersName: "",
      conceptionDate: "",
      expectedBirthDate: "",
      gestation: { ultrasoundDate: "", medicalProfessional: "" },
      representative: { ...RepresentativeSchema },
    },
    bankruptcy: {
      companyName: "",
      cnpj: "",
      administrator: { ...RepresentativeSchema },
      decreeDate: "",
      universalCourt: "",
    },
  },

  location: {
    status: LOCATION_STATUS.VERIFIED,
    address: { ...AddressSchema },
    diligenceHistory: [],
  },

  financial: {
    isProBono: false,
    hasLegalAid: false,
    bankAccount: { bank: "", agency: "", account: "", pix: "" },
  },
};

export const createSafeClient = (rawData) => {
  if (!rawData) return null;

  const q = rawData?.qualification || {};
  const l = rawData?.location || {};
  const addr =
    l?.address || rawData?.address || rawData?.location?.address || {};
  const contact = rawData?.contact || {};
  const fallbackAddress = rawData?.fallbackAddress || {};

  const safeAddress = {
    street: String(addr.street || "").trim(),
    number: String(addr.number ?? "").trim(),
    complement: String(addr.complement ?? "").trim(),
    neighborhood: String(addr.neighborhood || "").trim(),
    city: String(addr.city || "").trim(),
    state: String(addr.state || "").trim(),
    zipCode: String(addr.zipCode || "").trim(),
  };

  const safeType =
    String(rawData?.type || CLIENT_TYPES.PHYSICAL).trim() ||
    CLIENT_TYPES.PHYSICAL;

  const repIdFromAnchor = String(rawData?.anchor?.id || "").trim();
  const repIdFromAnimal = String(
    q?.animal?.responsible?.id || q?.animal?.guardian?.id || "",
  ).trim();
  const repIdFromMinor = String(q?.minor?.representative?.id || "").trim();
  const repIdFromLegal = String(q?.legal?.representative?.id || "").trim();
  const repIdFromEstate = String(q?.estate?.inventorier?.id || "").trim();
  const repIdFromCondo = String(q?.condominium?.syndic?.id || "").trim();
  const repIdFromUnborn = String(q?.unborn?.representative?.id || "").trim();
  const repIdFromImprisoned = String(q?.imprisoned?.proxy?.id || "").trim();
  const repIdFromBankruptcy = String(
    q?.bankruptcy?.administrator?.id || "",
  ).trim();

  const representativeId =
    repIdFromAnimal ||
    repIdFromMinor ||
    repIdFromLegal ||
    repIdFromEstate ||
    repIdFromCondo ||
    repIdFromUnborn ||
    repIdFromImprisoned ||
    repIdFromBankruptcy ||
    repIdFromAnchor ||
    "";

  const phys = q?.physical || {};

  return {
    ...rawData,
    id: String(rawData?.id || rawData?.clientId || "").trim(),
    type: safeType,
    qualification: q,
    location: {
      ...(l || {}),
      address: safeAddress,
    },
    contact: {
      ...(contact || {}),
      email: String(contact?.email || rawData?.contact?.email || "").trim(),
      phonePrimary: String(
        contact?.phonePrimary ||
          contact?.phone ||
          rawData?.contact?.phonePrimary ||
          rawData?.contact?.phone ||
          "",
      ).trim(),
    },
    __safe: {
      representativeId,
      name:
        String(phys?.socialName || phys?.name || rawData?.name || "").trim() ||
        "NOME NÃƒO INFORMADO",
    },
  };
};

export const buildRepresentativeFromClient = (
  clientData,
  fallbackRepresentative = {},
) => {
  if (!clientData && !fallbackRepresentative) return null;

  const source = clientData || {};
  const fallback = fallbackRepresentative || {};
  const physical = source?.qualification?.physical || {};
  const topLevelAddress = source?.location?.address || source?.address || {};
  const fallbackAddress = fallback?.address || {};
  const contact = source?.contact || {};
  const fallbackContact = fallback?.contact || {};

  return {
    ...(fallback || {}),
    id: String(source?.id || source?.clientId || fallback?.id || "").trim(),
    name: String(
      physical?.socialName ||
        physical?.name ||
        source?.name ||
        fallback?.name ||
        "",
    ).trim(),
    socialName: String(
      physical?.socialName || source?.socialName || fallback?.socialName || "",
    ).trim(),
    pronoun: String(
      physical?.pronoun || source?.pronoun || fallback?.pronoun || "",
    ).trim(),
    grammaticalGender: String(
      physical?.grammaticalGender ||
        source?.grammaticalGender ||
        fallback?.grammaticalGender ||
        "",
    ).trim(),
    gender: String(
      physical?.grammaticalGender || source?.gender || fallback?.gender || "",
    ).trim(),
    birthDate: String(
      physical?.birthDate || source?.birthDate || fallback?.birthDate || "",
    ).trim(),
    motherName: String(
      physical?.motherName || source?.motherName || fallback?.motherName || "",
    ).trim(),
    fatherName: String(
      physical?.fatherName || source?.fatherName || fallback?.fatherName || "",
    ).trim(),
    approximateAgeMonths: String(
      physical?.approximateAgeMonths ||
        source?.approximateAgeMonths ||
        fallback?.approximateAgeMonths ||
        "",
    ).trim(),
    nationality: String(
      physical?.nationality ||
        source?.nationality ||
        fallback?.nationality ||
        "",
    ).trim(),
    naturalness: String(
      physical?.naturalness ||
        source?.naturalness ||
        fallback?.naturalness ||
        "",
    ).trim(),
    civilStatus: String(
      physical?.civilStatus ||
        source?.civilStatus ||
        fallback?.civilStatus ||
        "",
    ).trim(),
    maritalRegime: String(
      physical?.maritalRegime ||
        source?.maritalRegime ||
        fallback?.maritalRegime ||
        "",
    ).trim(),
    spouseName: String(
      physical?.spouseName || source?.spouseName || fallback?.spouseName || "",
    ).trim(),
    spouseCPF: String(
      physical?.spouseCPF || source?.spouseCPF || fallback?.spouseCPF || "",
    ).trim(),
    profession: String(
      physical?.profession || source?.profession || fallback?.profession || "",
    ).trim(),
    rg: String(physical?.rg || source?.rg || fallback?.rg || "").trim(),
    orgaoEmissor: String(
      physical?.orgaoEmissor ||
        source?.orgaoEmissor ||
        source?.rgIssuer ||
        fallback?.orgaoEmissor ||
        fallback?.rgIssuer ||
        "",
    ).trim(),
    cpf: String(physical?.cpf || source?.cpf || fallback?.cpf || "").trim(),
    hasProfessionalRegister: Boolean(
      physical?.hasProfessionalRegister ||
      source?.hasProfessionalRegister ||
      fallback?.hasProfessionalRegister,
    ),
    registerType: String(
      physical?.registerType ||
        source?.registerType ||
        fallback?.registerType ||
        "",
    ).trim(),
    registerUf: String(
      physical?.registerUf || source?.registerUf || fallback?.registerUf || "",
    ).trim(),
    professionalRegister: String(
      physical?.professionalRegister ||
        source?.professionalRegister ||
        fallback?.professionalRegister ||
        source?.oab ||
        fallback?.oab ||
        "",
    ).trim(),
    address: {
      ...(fallbackAddress || {}),
      ...(topLevelAddress || {}),
    },
    contact: {
      ...(fallbackContact || {}),
      ...(contact || {}),
      phonePrimary: String(
        contact?.phonePrimary ||
          contact?.phone ||
          fallbackContact?.phonePrimary ||
          fallbackContact?.phone ||
          "",
      ).trim(),
      phoneSecondary: String(
        contact?.phoneSecondary || fallbackContact?.phoneSecondary || "",
      ).trim(),
      phone: String(
        contact?.phonePrimary ||
          contact?.phone ||
          fallbackContact?.phonePrimary ||
          fallbackContact?.phone ||
          "",
      ).trim(),
      email: String(contact?.email || fallbackContact?.email || "").trim(),
    },
  };
};

export const hydrateClientData = async (clientData) => {
  if (!clientData) return null;

  const hydrated = structuredClone
    ? structuredClone(clientData)
    : JSON.parse(JSON.stringify(clientData));
  const safeBase = createSafeClient(hydrated);
  if (!safeBase) return hydrated;

  const appId = String(FIREBASE_CONFIG?.projectId || "").trim();
  const repId = String(safeBase?.__safe?.representativeId || "").trim();
  if (!appId || !repId) return hydrated;

  try {
    const repRaw = await fetchClientDoc(appId, repId);
    if (repRaw && repRaw.name !== "CLIENTE NÃO ENCONTRADO") {
      const repSafeClient = createSafeClient(repRaw);
      hydrated.representative = repSafeClient;

      const rep = buildRepresentativeFromClient(repSafeClient);
      if (rep) {
        const type = String(hydrated?.type || "").trim();
        if (type === CLIENT_TYPES.ANIMAL) {
          if (!hydrated.qualification) hydrated.qualification = {};
          if (!hydrated.qualification.animal)
            hydrated.qualification.animal = {};
          hydrated.qualification.animal.responsible = rep;
        }
        if (type === CLIENT_TYPES.MINOR) {
          if (!hydrated.qualification) hydrated.qualification = {};
          if (!hydrated.qualification.minor) hydrated.qualification.minor = {};
          hydrated.qualification.minor.representative = rep;
        }
        if (type === CLIENT_TYPES.LEGAL) {
          if (!hydrated.qualification) hydrated.qualification = {};
          if (!hydrated.qualification.legal) hydrated.qualification.legal = {};
          hydrated.qualification.legal.representative = rep;
        }
        if (type === CLIENT_TYPES.ESTATE) {
          if (!hydrated.qualification) hydrated.qualification = {};
          if (!hydrated.qualification.estate)
            hydrated.qualification.estate = {};
          hydrated.qualification.estate.inventorier = rep;
        }
        if (type === CLIENT_TYPES.CONDOMINIUM) {
          if (!hydrated.qualification) hydrated.qualification = {};
          if (!hydrated.qualification.condominium)
            hydrated.qualification.condominium = {};
          hydrated.qualification.condominium.syndic = rep;
        }
        if (type === CLIENT_TYPES.UNBORN || type === CLIENT_TYPES.NASCITURO) {
          if (!hydrated.qualification) hydrated.qualification = {};
          if (!hydrated.qualification.unborn)
            hydrated.qualification.unborn = {};
          hydrated.qualification.unborn.representative = rep;
        }
        if (type === CLIENT_TYPES.IMPRISONED) {
          if (!hydrated.qualification) hydrated.qualification = {};
          if (!hydrated.qualification.imprisoned)
            hydrated.qualification.imprisoned = {};
          hydrated.qualification.imprisoned.proxy = rep;
        }
        if (type === CLIENT_TYPES.BANKRUPTCY) {
          if (!hydrated.qualification) hydrated.qualification = {};
          if (!hydrated.qualification.bankruptcy)
            hydrated.qualification.bankruptcy = {};
          hydrated.qualification.bankruptcy.administrator = rep;
        }
      }
    } else {
      hydrated.representative = createSafeClient({
        id: repId,
        name: "REPRESENTANTE NÃƒO ENCONTRADO",
      });
    }
  } catch {
    hydrated.representative = createSafeClient({
      id: repId,
      name: "ERRO DE HIDRATAÃ‡ÃƒO",
    });
  }

  return hydrated;
};

export const mergeRepresentativeFromLiveClient = (liveRep, currentRep = {}) =>
  buildRepresentativeFromClient(liveRep, currentRep);

export const resolveCanonicalClientForQualification = (
  clientData,
  liveClientsById = {},
) => {
  if (!clientData) return clientData;

  try {
    const clone = cloneDeepSafe(clientData);
    const type = String(clone?.type || "").trim();
    if (!type) return clone;

    const entries = Object.entries(liveClientsById || {});
    const findClientIdByCpf = (cpf) => {
      const target = digitsOnly(cpf);
      if (!target || target.length !== 11) return "";
      for (const [id, c] of entries) {
        const liveCpf = digitsOnly(
          c?.qualification?.physical?.cpf || c?.cpf || "",
        );
        if (liveCpf && liveCpf === target) return String(id);
      }
      return "";
    };

    const getLinkedRepId = (repLike) => {
      const direct = String(repLike?.id || "").trim();
      if (direct) return direct;

      const anchorId = String(clone?.anchor?.id || "").trim();
      if (anchorId) return anchorId;

      const repByCpf = findClientIdByCpf(repLike?.cpf || "");
      if (repByCpf) return repByCpf;

      const anchorByCpf = findClientIdByCpf(clone?.anchor?.cpf || "");
      if (anchorByCpf) return anchorByCpf;

      return "";
    };

    const injectRep = (getCurrentRep, setRep) => {
      const currentRep = getCurrentRep();
      const refId = getLinkedRepId(currentRep);
      if (!refId) return;
      const liveRep = liveClientsById?.[refId];
      if (!liveRep) return;
      setRep(mergeRepresentativeFromLiveClient(liveRep, currentRep));
    };

    if (type === CLIENT_TYPES.ANIMAL) {
      injectRep(
        () =>
          clone?.qualification?.animal?.responsible ||
          clone?.qualification?.animal?.guardian ||
          {},
        (rep) => {
          if (!clone.qualification) clone.qualification = {};
          if (!clone.qualification.animal) clone.qualification.animal = {};
          clone.qualification.animal.responsible = rep;
        },
      );
    }

    if (type === CLIENT_TYPES.MINOR) {
      injectRep(
        () => clone?.qualification?.minor?.representative || {},
        (rep) => {
          if (!clone.qualification) clone.qualification = {};
          if (!clone.qualification.minor) clone.qualification.minor = {};
          clone.qualification.minor.representative = rep;
        },
      );
    }

    if (type === CLIENT_TYPES.LEGAL) {
      injectRep(
        () => clone?.qualification?.legal?.representative || {},
        (rep) => {
          if (!clone.qualification) clone.qualification = {};
          if (!clone.qualification.legal) clone.qualification.legal = {};
          clone.qualification.legal.representative = rep;
        },
      );
    }

    if (type === CLIENT_TYPES.ESTATE) {
      injectRep(
        () =>
          clone?.qualification?.estate?.inventorier ||
          clone?.qualification?.estate?.representative ||
          clone?.qualification?.estate?.inventor ||
          {},
        (rep) => {
          if (!clone.qualification) clone.qualification = {};
          if (!clone.qualification.estate) clone.qualification.estate = {};
          clone.qualification.estate.inventorier = rep;
        },
      );
    }

    if (type === CLIENT_TYPES.CONDOMINIUM) {
      injectRep(
        () => clone?.qualification?.condominium?.syndic || {},
        (rep) => {
          if (!clone.qualification) clone.qualification = {};
          if (!clone.qualification.condominium)
            clone.qualification.condominium = {};
          clone.qualification.condominium.syndic = rep;
        },
      );
    }

    if (type === CLIENT_TYPES.UNBORN || type === CLIENT_TYPES.NASCITURO) {
      injectRep(
        () => clone?.qualification?.unborn?.representative || {},
        (rep) => {
          if (!clone.qualification) clone.qualification = {};
          if (!clone.qualification.unborn) clone.qualification.unborn = {};
          clone.qualification.unborn.representative = rep;
        },
      );
    }

    if (type === CLIENT_TYPES.IMPRISONED) {
      injectRep(
        () => clone?.qualification?.imprisoned?.proxy || {},
        (rep) => {
          if (!clone.qualification) clone.qualification = {};
          if (!clone.qualification.imprisoned)
            clone.qualification.imprisoned = {};
          clone.qualification.imprisoned.proxy = rep;
        },
      );
    }

    if (type === CLIENT_TYPES.BANKRUPTCY) {
      injectRep(
        () => clone?.qualification?.bankruptcy?.administrator || {},
        (rep) => {
          if (!clone.qualification) clone.qualification = {};
          if (!clone.qualification.bankruptcy)
            clone.qualification.bankruptcy = {};
          clone.qualification.bankruptcy.administrator = rep;
        },
      );
    }

    return clone;
  } catch {
    return clientData;
  }
};

export const buildCanonicalClientQualification = (
  clientData,
  liveClientsById = {},
  options = {},
) => {
  const canonicalClient = resolveCanonicalClientForQualification(
    clientData,
    liveClientsById,
  );
  return {
    client: canonicalClient,
    text: String(
      generateQualificationText(canonicalClient, options) || "",
    ).trim(),
  };
};

export const hydrateAndBuildCanonicalClientQualification = async (
  clientData,
  liveClientsById = {},
  options = {},
) => {
  const hydrated = await hydrateClientData(clientData);
  return buildCanonicalClientQualification(
    hydrated || clientData,
    liveClientsById,
    options,
  );
};

/**
 * @deprecated Use o cliente bruto com `hydrateAndBuildCanonicalClientQualification(...)`.
 */
export const buildLawyerQualificationPayload = (source = {}, fallback = {}) => {
  const merged = { ...(fallback || {}), ...(source || {}) };
  const physical = {
    ...((fallback || {})?.qualification?.physical || {}),
    ...((source || {})?.qualification?.physical || {}),
  };
  const professional = {
    ...((fallback || {})?.professional || {}),
    ...((source || {})?.professional || {}),
  };
  const contact = {
    ...((fallback || {})?.contact || {}),
    ...((source || {})?.contact || {}),
  };
  const locationAddress = {
    ...(((fallback || {})?.location || {})?.address || {}),
    ...(((source || {})?.location || {})?.address || {}),
  };

  const phonePrimary = String(
    contact?.phonePrimary ||
      merged?.phonePrimary ||
      contact?.phone ||
      merged?.phone ||
      "",
  ).trim();
  const phoneSecondary = String(
    contact?.phoneSecondary || merged?.phoneSecondary || "",
  ).trim();
  const email = String(contact?.email || merged?.email || "").trim();
  const address = {
    street: String(
      merged?.address?.street ||
        locationAddress?.street ||
        merged?.addressStreet ||
        "",
    ).trim(),
    number: String(
      merged?.address?.number ??
        locationAddress?.number ??
        merged?.addressNumber ??
        "",
    ).trim(),
    complement: String(
      merged?.address?.complement ??
        locationAddress?.complement ??
        merged?.addressComplement ??
        "",
    ).trim(),
    neighborhood: String(
      merged?.address?.neighborhood ||
        locationAddress?.neighborhood ||
        merged?.addressNeighborhood ||
        "",
    ).trim(),
    city: String(
      merged?.address?.city ||
        locationAddress?.city ||
        merged?.addressCity ||
        "",
    ).trim(),
    state: String(
      merged?.address?.state ||
        locationAddress?.state ||
        merged?.addressState ||
        professional?.oabState ||
        merged?.oabState ||
        "",
    ).trim(),
    zipCode: String(
      merged?.address?.zipCode ||
        locationAddress?.zipCode ||
        merged?.addressZip ||
        "",
    ).trim(),
  };

  if (!address.street && String(merged?.fullAddress || "").trim()) {
    address.street = String(merged.fullAddress || "").trim();
  }

  return {
    type: CLIENT_TYPES.PHYSICAL,
    healthCondition: [],
    isFullDependent: false,
    contact: {
      email,
      phonePrimary,
      phoneSecondary,
      phone: phonePrimary,
      isDigitalNative: true,
    },
    location: {
      status: LOCATION_STATUS.VERIFIED,
      address,
    },
    qualification: {
      physical: {
        name: String(physical?.name || merged?.name || "").trim(),
        socialName: "",
        pronoun: String(physical?.pronoun || merged?.pronoun || "").trim(),
        genderIdentity: String(
          physical?.genderIdentity || merged?.genderIdentity || "",
        ).trim(),
        grammaticalGender: String(
          physical?.grammaticalGender ||
            merged?.grammaticalGender ||
            merged?.gender ||
            "",
        ).trim(),
        birthDate: String(
          physical?.birthDate || merged?.birthDate || "",
        ).trim(),
        motherName: String(
          physical?.motherName || merged?.motherName || "",
        ).trim(),
        fatherName: String(
          physical?.fatherName || merged?.fatherName || "",
        ).trim(),
        nationality: String(
          physical?.nationality || merged?.nationality || "",
        ).trim(),
        naturalness: String(
          physical?.naturalness || merged?.naturalness || "",
        ).trim(),
        civilStatus: String(
          physical?.civilStatus || merged?.civilStatus || "",
        ).trim(),
        maritalRegime: String(
          physical?.maritalRegime || merged?.maritalRegime || "",
        ).trim(),
        spouseName: String(
          physical?.spouseName || merged?.spouseName || "",
        ).trim(),
        spouseCPF: String(
          physical?.spouseCPF || merged?.spouseCPF || "",
        ).trim(),
        profession: String(
          physical?.profession || merged?.profession || "",
        ).trim(),
        hasProfessionalRegister: Boolean(
          physical?.hasProfessionalRegister ||
          merged?.hasProfessionalRegister ||
          professional?.oab ||
          merged?.oab ||
          physical?.professionalRegister ||
          merged?.professionalRegister,
        ),
        registerType: String(
          physical?.registerType ||
            merged?.registerType ||
            (professional?.oab || merged?.oab ? "OAB" : ""),
        ).trim(),
        registerUf: String(
          physical?.registerUf ||
            merged?.registerUf ||
            professional?.oabState ||
            merged?.oabState ||
            "",
        ).trim(),
        professionalRegister: String(
          physical?.professionalRegister ||
            merged?.professionalRegister ||
            professional?.oab ||
            merged?.oab ||
            "",
        ).trim(),
        isPPE: Boolean(physical?.isPPE || merged?.isPPE),
        ppeDescription: String(
          physical?.ppeDescription || merged?.ppeDescription || "",
        ).trim(),
        rg: String(physical?.rg || merged?.rg || "").trim(),
        orgaoEmissor: String(
          physical?.orgaoEmissor ||
            merged?.orgaoEmissor ||
            merged?.rgIssuer ||
            "",
        ).trim(),
        cpf: String(physical?.cpf || merged?.cpf || "").trim(),
      },
    },
  };
};

const fmtCnpj = (value) => {
  const d = String(value || "").replace(/\D/g, "");
  if (d.length !== 14) return String(value || "").trim();
  const val = d.replace(
    /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
    "$1.$2.$3/$4-$5",
  );
  return val ? `sob o n. ${val}` : "";
};

const fmtCpfNum = (value) => {
  const d = String(value || "").replace(/\D/g, "");
  if (d.length !== 11) return String(value || "").trim();
  return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
};

const fmtRg = (value) => {
  const d = String(value || "").replace(/\D/g, "");
  return d.length > 4 ? d : value || "";
};

const fmtRgNum = (value) => {
  const d = String(value || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.length === 8)
    return `${d.slice(0, 1)}.${d.slice(1, 4)}.${d.slice(4, 7)}-${d.slice(7)}`;
  if (d.length === 9)
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}-${d.slice(8)}`;
  return d;
};

const fmtDate = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const br = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (br) return raw;
  return raw;
};

const fmtAge = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const br = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  const year = iso ? Number(iso[1]) : br ? Number(br[3]) : NaN;
  const month = iso ? Number(iso[2]) : br ? Number(br[2]) : NaN;
  const day = iso ? Number(iso[3]) : br ? Number(br[1]) : NaN;
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day)
  )
    return "";
  const birth = new Date(year, month - 1, day);
  if (Number.isNaN(birth.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - year;
  const hasHadBirthday =
    today.getMonth() > month - 1 ||
    (today.getMonth() === month - 1 && today.getDate() >= day);
  if (!hasHadBirthday) age -= 1;
  if (!Number.isFinite(age) || age < 0 || age > 130) return "";
  return String(age);
};

const UF_TO_STATE = {
  AC: "Acre",
  AL: "Alagoas",
  AP: "Amapá",
  AM: "Amazonas",
  BA: "Bahia",
  CE: "Ceará",
  DF: "Distrito Federal",
  ES: "Espírito Santo",
  GO: "Goiás",
  MA: "Maranhão",
  MT: "Mato Grosso",
  MS: "Mato Grosso do Sul",
  MG: "Minas Gerais",
  PA: "Pará",
  PB: "Paraíba",
  PR: "Paraná",
  PE: "Pernambuco",
  PI: "Piauí",
  RJ: "Rio de Janeiro",
  RN: "Rio Grande do Norte",
  RS: "Rio Grande do Sul",
  RO: "Rondônia",
  RR: "Roraima",
  SC: "Santa Catarina",
  SP: "São Paulo",
  SE: "Sergipe",
  TO: "Tocantins",
};

// Mapa de preposicao para cada UF (genero do estado)
// "da" para estados femininos, "de" para neutros/sem artigo, "do" para masculinos
const STATE_PREPOSITION = {
  AC: "do",
  AL: "de",
  AP: "do",
  AM: "do",
  BA: "da",
  CE: "do",
  DF: "do",
  ES: "de",
  GO: "de",
  MA: "do",
  MT: "do",
  MS: "do",
  MG: "de",
  PA: "do",
  PB: "da",
  PR: "do",
  PE: "de",
  PI: "do",
  RJ: "do",
  RN: "do",
  RS: "do",
  RO: "de",
  RR: "de",
  SC: "de",
  SP: "de",
  SE: "de",
  TO: "do",
};

const resolveStatePrep = (uf) => {
  const ufUp = String(uf || "")
    .trim()
    .toUpperCase();
  return STATE_PREPOSITION[ufUp] || "do";
};

const fmtRgIssuer = (orgaoEmissor, uf) => {
  const raw = String(orgaoEmissor || "").trim();
  if (!raw) return "";
  const up = raw.toUpperCase();
  const ufUp = String(uf || "")
    .trim()
    .toUpperCase();

  const isSspLegacy =
    up === "SSP" ||
    up === `SSP/${ufUp}` ||
    (/^SSP\s*\(/i.test(raw) &&
      up.includes("SECRETARIA") &&
      up.includes("SEGURAN"));

  if (isSspLegacy) {
    const stateName = UF_TO_STATE[ufUp] || ufUp;
    const prep = resolveStatePrep(ufUp);
    return `Secretaria de Segurança Pública do Estado ${prep} ${stateName} (SSP/${ufUp})`;
  }

  if (/^SSP\s*\(/i.test(raw)) return raw;
  if (/^SSP\/[A-Z]{2}$/i.test(up)) return raw;

  return raw;
};

// Preposição "pelo"/"pela" na frase "emitido(a) ___ [órgão]" depende do
// gênero gramatical do ÓRGÃO (masculino: "o Instituto" -> pelo; feminino:
// "a Secretaria" -> pela) — INDEPENDENTE do gênero do titular do documento
// ou do documento em si. Achado real (24/08/2026): "Instituto de
// Identificação do Paraná" (órgão comum da CIN) é masculino; o texto de RG
// só nunca tinha exposto esse bug porque o único exemplo testado era
// "Secretaria de Segurança Pública" (feminino). Heurística pelas primeiras
// palavras — cobre os órgãos emissores de identidade mais comuns no Brasil;
// não pretende ser um analisador gramatical genérico.
const MASCULINE_ISSUER_HEADWORDS = [
  "instituto",
  "departamento",
  "serviço",
  "servico",
  "ministério",
  "ministerio",
  "corpo",
  "conselho",
  "gabinete",
];

const resolveIssuerPreposition = (orgaoEmissor) => {
  const firstWord = String(orgaoEmissor || "")
    .trim()
    .toLowerCase()
    .split(/\s+/)[0];
  return MASCULINE_ISSUER_HEADWORDS.includes(firstWord) ? "pelo" : "pela";
};

const hasEmbeddedAddressNumber = (street) => {
  const s = String(street || "").trim();
  if (!s) return false;
  return (
    /\b(?:n\.?\s*[ÂºÂ°]?\s*|número)\s*\d+/i.test(s) ||
    /,\s*\d+[A-Z0-9-]*/i.test(s)
  );
};

const escapeRegExp = (value) =>
  String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const sanitizeStreetForStructuredNumber = (street, rawNum) => {
  const s = String(street || "")
    .trim()
    .replace(/[\s,.;-]+$/g, "");
  if (!s) return "";
  const num = String(rawNum || "").trim();
  const genericTail =
    /(?:,|\s)+(?:n\.?\s*[ÂºÂ°]?|nr\.?|num\.?|número|n[Â°Âº]|no\.)\s*$/i;
  if (!num) return s.replace(genericTail, "").trim();
  const specificTail = new RegExp(
    `(?:,|\\s)+(?:n\\.?\\s*[ÂºÂ°]?|nr\\.?|num\\.?|número|n[Â°Âº]|no\\.)\\s*${escapeRegExp(num)}$`,
    "i",
  );
  const bareTail = new RegExp(`,\\s*${escapeRegExp(num)}$`, "i");
  return s
    .replace(specificTail, "")
    .replace(genericTail, "")
    .replace(bareTail, "")
    .trim();
};

const fmtAddr = (addr) => {
  if (!addr || !addr.street) return "[Endereço não informado]";
  const streetRaw = String(addr.street || "").trim();
  const rawCep = String(addr.zipCode || "").replace(/\D/g, "");
  const cep =
    rawCep.length === 8
      ? `${rawCep.slice(0, 2)}.${rawCep.slice(2, 5)}-${rawCep.slice(5)}`
      : addr.zipCode || "";

  const rawNum = String(addr.number ?? "").trim();
  const street = sanitizeStreetForStructuredNumber(streetRaw, rawNum);
  const shouldSuppressAutoNumber =
    hasEmbeddedAddressNumber(streetRaw) && !rawNum;
  const numero = shouldSuppressAutoNumber
    ? ""
    : !rawNum || rawNum === "0"
      ? "s/n"
      : /^s\s*\/\s*n$/i.test(rawNum) ||
          /^s\s*n$/i.test(rawNum) ||
          rawNum.toLowerCase() === "sn"
        ? "s/n"
        : /^n\.?\s*[ÂºÂ°]?/i.test(rawNum) ||
            /^nÂº/i.test(rawNum) ||
            /^s\/n$/i.test(rawNum)
          ? rawNum
          : `n. ${rawNum}`;
  const rawCompl = String(addr.complement ?? "").trim();
  const parts = [
    street || streetRaw,
    numero,
    !rawCompl || rawCompl === "0" ? "" : rawCompl,
    addr.neighborhood ? `bairro ${addr.neighborhood}` : "",
    [addr.city, addr.state].filter(Boolean).join("/"),
    cep ? `CEP: ${cep}` : "",
  ].filter(Boolean);
  return parts
    .join(", ")
    .replace(/nÂº\s*nÂº/gi, "n.")
    .replace(/n\.\s*nÂº/gi, "n.")
    .replace(/,\s*s\/n\s*,/gi, ", ")
    .replace(/,\s*,/g, ",")
    .trim();
};

const normalizeProfessionGender = (rawProf, gender) => {
  const prof = String(rawProf || "").trim();
  if (!prof) return "";
  const p = prof.toLowerCase();
  if (p === "advogado" || p === "advogada")
    return gender === "F" ? "advogada" : "advogado";
  return prof;
};

const formatProfessionalRegister = (
  registerType,
  registerUf,
  professionalRegister,
  gender,
) => {
  const rawType = String(registerType || "")
    .trim()
    .replace(/\s*\([^)]*\)\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const uf = String(registerUf || "")
    .trim()
    .toUpperCase();
  const number = String(professionalRegister || "").trim();
  if (!number) return "";

  // Tenta usar o dicionario REGISTER_PROVIDERS (ClientConstants.js)
  // para gerar o texto completo do conselho por extenso.
  // Ex: "inscrito no Conselho Regional de Engenharia e Agronomia do Parana (CREA/PR) sob o n. 123456"
  // Extrai apenas a sigla do conselho, ignorando sufixos:
  // "CREA/PR" -> "CREA" | "CREA (Engenharia/Agronomia)" -> "CREA" | "OAB/PR" -> "OAB"
  const councilKey = rawType
    .replace(/[/\s].*$/, "")
    .trim()
    .toUpperCase();
  const provider = REGISTER_PROVIDERS?.[councilKey];
  if (provider && uf) {
    try {
      return provider.buildText(number, uf, gender);
    } catch {
      // fallback se buildText falhar
    }
  }
  if (provider) {
    try {
      const text = provider.buildText(number, uf || "BR");
      return text.replace(/\([A-Z]{2}\/[A-Z]{2,}\)/g, "").trim();
    } catch {
      // fallback
    }
  }

  // Fallback generico
  if (!rawType) return `inscrito(a) sob o registro profissional n. ${number}`;
  if (uf) return `inscrito(a) na ${rawType}/${uf} sob o n. ${number}`;
  return `inscrito(a) na ${rawType} sob o n. ${number}`;
};

const normalizeNaturalness = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  return raw
    .toLowerCase()
    .replace(/\s*\/\s*/g, "/")
    .replace(/\s*\([^)]*\)\s*/g, "")
    .replace(
      /(^|[\s\-\/\(])([a-zÃ -Ã¿])/g,
      (_, prefix, char) => `${prefix}${char.toUpperCase()}`,
    )
    .replace(
      /\/(\w{2})(?=$|\b)/g,
      (_, uf) => `/${String(uf || "").toUpperCase()}`,
    )
    .replace(/\b(Da|De|Do|Das|Dos|E)\b/g, (token) => token.toLowerCase());
};

const lower = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

// Todo valor aqui vem de campo digitado pelo usuário (nome de cliente,
// cônjuge, mãe do nascituro etc.) — precisa ser escapado antes de virar
// HTML confiável, senão um nome com "<script>" ou "onerror=" executaria
// no navegador de quem abrir o documento (dangerouslySetInnerHTML).
const strongUpper = (value) =>
  `<strong>${escapeHtml(
    String(value || "")
      .trim()
      .toUpperCase(),
  )}</strong>`;

const formatAnimalBreed = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  if (/^[A-Z]{2,}$/.test(raw)) return raw;

  return raw.toLowerCase();
};

const inflectInline = (value, gender = "M") => {
  const text = String(value || "").trim();
  if (!text) return "";
  const g =
    String(gender || "M")
      .trim()
      .toUpperCase() === "F"
      ? "F"
      : "M";
  return text
    .replace(
      /([A-Za-zÀ-ÿ]+)o\(a\)/g,
      (_, stem) => `${stem}${g === "F" ? "a" : "o"}`,
    )
    .replace(
      /([A-Za-zÀ-ÿ]+)os\(as\)/g,
      (_, stem) => `${stem}${g === "F" ? "as" : "os"}`,
    )
    .replace(
      /([A-Za-zÀ-ÿ]+r)\(a\)/g,
      (_, stem) => `${stem}${g === "F" ? "a" : ""}`,
    )
    .replace(/\bdo\(a\)\b/gi, g === "F" ? "da" : "do")
    .replace(/\bno\(a\)\b/gi, g === "F" ? "na" : "no")
    .replace(/\(a\)/gi, g === "F" ? "a" : "o")
    .replace(/\(o\)/gi, g === "F" ? "a" : "o")
    .replace(/\(as\)/gi, g === "F" ? "as" : "os")
    .replace(/\(os\)/gi, g === "F" ? "as" : "os")
    .replace(/\(da\)/gi, g === "F" ? "da" : "do")
    .replace(/\(do\)/gi, g === "F" ? "da" : "do")
    .replace(/\(das\)/gi, g === "F" ? "das" : "dos")
    .replace(/\(dos\)/gi, g === "F" ? "das" : "dos");
};

const normGender = (grammaticalGender, pronoun, hints = {}) => {
  const p = String(pronoun || "")
    .trim()
    .toUpperCase();
  if (
    p.includes("(A)") ||
    [
      "A",
      "ELA",
      "SRA.",
      "SRA",
      "SENHORA",
      "DRA.",
      "DRA",
      "DOUTORA",
      "FÃŠMEA",
      "FEMEA",
    ].includes(p)
  )
    return "F";
  if (
    p.includes("(O)") ||
    [
      "O",
      "ELE",
      "SR.",
      "SR",
      "SENHOR",
      "DR.",
      "DR",
      "DOUTOR",
      "MACHO",
    ].includes(p)
  )
    return "M";

  const g = String(grammaticalGender || "")
    .trim()
    .toUpperCase();
  if (g === "F" || g === "M") return g;
  if (
    [
      "FEMININO",
      "FEM",
      "FEMALE",
      "MULHER",
      "MULHER CIS",
      "MULHER TRANS",
      "ELA",
      "FÃŠMEA",
      "FEMEA",
    ].includes(g)
  )
    return "F";
  if (
    [
      "MASCULINO",
      "MASC",
      "MALE",
      "HOMEM",
      "HOMEM CIS",
      "HOMEM TRANS",
      "ELE",
      "MACHO",
    ].includes(g)
  )
    return "M";

  const hintText = [hints?.nationality, hints?.civilStatus, hints?.profession]
    .map((value) =>
      String(value || "")
        .trim()
        .toLowerCase(),
    )
    .filter(Boolean)
    .join(" | ");

  if (
    /(brasileira|casada|divorciada|viúva|viuva|companheira|advogada|aposentada|professora)/i.test(
      hintText,
    )
  )
    return "F";
  if (
    /(brasileiro|casado|divorciado|viúvo|viuvo|companheiro|advogado|aposentado|professor)/i.test(
      hintText,
    )
  )
    return "M";

  return "M";
};

// ============================================================================
// PADRÃO OURO DE QUALIFICAÇÃO DE PESSOA FÍSICA (DEFINIDO EM 21/07/2026)
// ============================================================================
// 🟡 UNIFICAÇÃO CONCLUÍDA EM 21/07/2026 — Todos os 8 consumidores do Portal
// foram refatorados para usar exclusivamente este motor canônico via
// generateQualificationText(). NENHUMA lógica manual de qualificação deve
// existir fora deste arquivo.
//
// CONSUMIDORES UNIFICADOS (21/07/2026):
//   1. Bridge partes (BridgeClients.jsx)        — hydrateAndBuildCanonicalClientQualification
//   2. CRM salvar (ClientManager.jsx)            — generateQualificationText
//   3. FileHubArea (qualificação)               — hydrateAndBuildCanonicalClientQualification
//   4. Gerador de Procurações                   — buildCanonicalClientQualification
//   5. ContractEngine (Contratos)                — buildCanonicalClientQualification
//   6. ProcurationEngine (Procurações)           — buildCanonicalClientQualification
//   7. PetitionEngine (Petições)                 — generateQualificationText
//   8. CadernoCockpit (Caderno de Autos)         — generateQualificationText (via adaptador)
//
// REGRA: QUALQUER novo consumidor DEVE usar generateQualificationText()
// ou buildCanonicalClientQualification(). NUNCA criar lógica manual.
// Última atualização: 21/07/2026 — Rodrigo Demeterco.
// ============================================================================
//
// ORIGEM: 3 pesquisas independentes (R1, R2, R3) + validação MCP parallel-search
// CONFIRMADO POR: Art. 319, II, CPC + Aurum + ADVBOX + Projuris + JusDocs
//
// ORDEM DOS CAMPOS (13 posições):
//   1. NOME            → strongUpper(name)
//   2. NACIONALIDADE   → praxe forense (não está no CPC, mas universal)
//   3. ESTADO CIVIL    → Art. 319, II (+ união estável)
//   4. PROFISSÃO       → Art. 319, II
//   5. CONSELHO PROF.  → extensão lógica da profissão (REGISTER_PROVIDERS)
//   6. CPF             → Art. 319, II — ANTES do RG (decisão: documento fiscal é primário)
//   7. RG + emissor    → Portaria 71 TJDFT — SSP/PR por extenso
//   8. NATURALIDADE    → decisão do titular (diferencia homônimos)
//   9. DATA NASCIMENTO → decisão do titular (dado complementar forense)
//  10. IDADE           → calculada dinamicamente via fmtAge()
//  11. DOMICÍLIO       → rua, n., complemento, bairro, cidade/UF, CEP
//  12. E-MAIL          → "endereço eletrônico:" (separado do telefone)
//  13. TELEFONE        → "telefone/WhatsApp:"
//
// DECISÕES DE PROJETO (NÃO ALTERAR SEM REVISÃO):
//  - NATURALIDADE E DATA NASCIMENTO: sempre inclusos (não atrás de flag)
//  - CPF: texto completo "Cadastro de Pessoas Físicas (CPF) sob o n."
//  - RG: "Registro Geral (RG) n. X, emitido pela [orgaoEmissor extenso]"
//  - COMPLEMENTO ENDEREÇO: sempre exibido (bug corrigido: "casa" não era exibido)
//  - CAU: usa "Conselho de Arquitetura e Urbanismo" (SEM "Regional" — Lei 12.378/2010)
//  - CONSELHOS: 13 registrados em REGISTER_PROVIDERS (ClientConstants.js)
//  - EMISSORES RG: SSP → "Secretaria de Segurança Pública do Estado X (SSP/UF)"
//  - CIN (24/08/2026): item 7 bifurca por `identityDocType` ("RG"|"CIN").
//    RG tradicional mantém o texto de sempre. CIN usa EXATAMENTE o mesmo
//    padrão de frase do RG — "portador(a) da Carteira de Identidade
//    Nacional (CIN) n. X, emitida pela Y" —, só troca o nome do documento.
//    Duas tentativas anteriores (número explicado como "corresponde também
//    ao RG e ao Personal Number", depois "conforme Decreto n. 10.977/2022")
//    foram rejeitadas pelo titular por soarem como nota explicativa em vez
//    de qualificação jurídica seca — decisão final, 24/08/2026: nenhuma
//    cláusula extra, só a substituição do nome do documento. O número da
//    CIN é o próprio CPF (não existe RG numérico separado nesse modelo,
//    achado real na CIN da cliente [CLIENTE_A]), mas isso não
//    precisa aparecer no texto. Local/data de emissão/validade da CIN
//    ficam só no cadastro, sem efeito jurídico na qualificação. Ver
//    `renderPhysicalPersonText`.
// ============================================================================
const renderPhysicalPersonText = (physicalData, addressData, options = {}) => {
  const p = physicalData || {};
  const addr = addressData || {};
  const g = normGender(p.grammaticalGender, p.pronoun, {
    nationality: p.nationality,
    civilStatus: p.civilStatus,
    profession: p.profession,
  });

  let nameDisplay = strongUpper(p.name || "[Nome não informado]");
  if (p.socialName)
    nameDisplay = `${strongUpper(p.socialName)} (registrado(a) civilmente como ${strongUpper(p.name)})`;

  const civil = lower(
    inflectInline(p.civilStatus || "de estado civil desconhecido", g),
  );
  const hasMaritalContext = civil.includes("casado") || civil.includes("união");
  const maritalRegimePart =
    hasMaritalContext && p.maritalRegime
      ? `sob o regime de ${lower(p.maritalRegime)}`
      : "";

  const prof = lower(
    inflectInline(
      normalizeProfessionGender(p.profession || "de profissão ignorada", g),
      g,
    ),
  );
  const registerPart =
    p.hasProfessionalRegister && p.professionalRegister
      ? formatProfessionalRegister(
          p.registerType || "Conselho",
          p.registerUf,
          p.professionalRegister,
          g,
        )
      : "";

  // Campos omitidos por completo quando não preenchidos (em vez de inserir
  // placeholder "não informado(a)") — pedido do usuário 2026-08-16: data de
  // nascimento, idade e naturalidade só entram na qualificação se existirem.
  const birthDate = fmtDate(p.birthDate);
  const birthPart = birthDate ? `nascido(a) em ${birthDate}` : "";
  const age = fmtAge(p.birthDate);
  const agePart = age ? `com ${age} anos` : "";
  const naturalnessNorm = normalizeNaturalness(p.naturalness);
  const naturalness = naturalnessNorm
    ? inflectInline(`natural de ${naturalnessNorm}`, g)
    : "";
  // Documento de identidade: RG tradicional (número próprio + órgão emissor
  // estadual) OU CIN (Carteira de Identidade Nacional, modelo gov.br
  // unificado — o "Registro Geral" impresso na CIN É o próprio CPF, não
  // existe numeração de RG separada nesse modelo; achado real ao qualificar
  // a cliente [CLIENTE_A], 24/08/2026, cuja CIN mostra o campo
  // "Registro Geral - CPF / Personal Number" como um único dado).
  const identityDocType =
    String(p.identityDocType || "RG")
      .trim()
      .toUpperCase() === "CIN"
      ? "CIN"
      : "RG";
  const cpfNum = fmtCpfNum(p.cpf);
  const cpfPart = cpfNum
    ? `inscrito(a) no Cadastro de Pessoas Físicas (CPF) sob o n. ${cpfNum}`
    : "CPF não informado ou desconhecido";
  // CIN: mesmo padrão de frase do RG, só troca o nome do documento —
  // "portador(a) do Registro Geral" vira "portador(a) da Carteira de
  // Identidade Nacional". O número da CIN é o próprio CPF (não existe RG
  // numérico separado nesse modelo), mas a frase não precisa dizer isso —
  // decisão do titular, 24/08/2026: nada de cláusula explicativa, seguir
  // literalmente o padrão já usado no RG. Data de emissão/validade/local
  // da CIN ficam só no cadastro (não têm efeito jurídico na qualificação).
  let identityPart = "";
  if (identityDocType === "CIN") {
    const cinNum = fmtCpfNum(p.cpf);
    const cinIssuer = fmtRgIssuer(p.cinOrgaoExpedidor, addr?.state);
    const cinPrep = resolveIssuerPreposition(cinIssuer);
    identityPart = cinNum
      ? `portador(a) da Carteira de Identidade Nacional (CIN) n. ${cinNum}${cinIssuer ? `, emitida ${cinPrep} ${cinIssuer}` : ""}`
      : "";
  } else {
    const rgNum = fmtRgNum(p.rg);
    const rgIssuer = fmtRgIssuer(p.orgaoEmissor, addr?.state);
    const rgPrep = resolveIssuerPreposition(rgIssuer);
    identityPart = rgNum
      ? `portador(a) do Registro Geral (RG) n. ${rgNum}${rgIssuer ? `, emitido ${rgPrep} ${rgIssuer}` : ""}`
      : "";
  }
  const nat = lower(
    inflectInline(p.nationality || "[Nacionalidade não informada]", g),
  );
  const spousePart =
    options?.includeSpouse && p.spouseName
      ? `cônjuge ${strongUpper(p.spouseName)}, inscrito(a) no CPF sob o n. ${fmtCpfNum(p.spouseCPF) || "CPF não informado ou desconhecido"}`
      : "";
  const ppePart =
    p.isPPE === true
      ? `pessoa politicamente exposta${p.ppeDescription ? ` (${String(p.ppeDescription).trim()})` : ""}`
      : "";

  // Ordem conforme Padrão Ouro (R3 — Art. 319, II, CPC):
  // 1. NOME → 2. NAC → 3. CIVIL → 4. UNIÃO → 5. PROF → 6. CONSELHO
  // → 7. CPF → 8. RG → 9. EMAIL → 10. DOMICÍLIO → 11. TEL
  const baseParts = [
    nameDisplay,
    nat,
    civil,
    ...(options?.includeSpouse ? [maritalRegimePart, spousePart] : []),
    prof,
    registerPart,
    cpfPart,
    identityPart,
    birthPart,
    agePart,
    naturalness,
    ppePart,
  ].filter(Boolean);

  // isAttorneyOfRecord: qualificação do(a) próprio(a) advogado(a)/escritório
  // como parte OUTORGADA/CONTRATADA de um instrumento (procuração, contrato).
  // Nesse caso o endereço declinado é também o escritório profissional para
  // fins de citação/intimação, diferente da qualificação comum de um cliente
  // qualquer. Não citar art. 106, I, CPC aqui nem em petição alguma em nome de
  // cliente - esse artigo só se aplica quando o advogado postula EM CAUSA
  // PRÓPRIA (ele mesmo como parte do litígio), o que não é o caso de uma
  // procuração/contrato de honorários nem de uma petição em nome do cliente
  // (bug real encontrado e corrigido em 19/08/2026 - citação incorreta estava
  // sendo gerada por este trecho e vazou para uma petição real).
  const isAttorneyOfRecord = Boolean(options?.isAttorneyOfRecord);

  const suppressAddress = Boolean(options?.suppressAddress);
  const sameAddress = Boolean(options?.sameAddress);
  const domicileVerb = isAttorneyOfRecord
    ? "residente, domiciliado(a) e com escritório profissional"
    : "residente e domiciliado(a)";
  const addressPart = !suppressAddress
    ? options?.locationStatus === LOCATION_STATUS.UNKNOWN
      ? "atualmente em lugar incerto e não sabido"
      : `${domicileVerb} na ${fmtAddr(addr)}`
    : sameAddress
      ? "residente e domiciliado(a) no mesmo endereço"
      : "";

  const email = String(options?.contact?.email || "").trim();
  const suppressContact = Boolean(options?.suppressContact);
  const emailDisplay = email || "sem endereço eletrônico conhecido";
  const emailPart = !suppressContact
    ? `endereço eletrônico: ${emailDisplay}`
    : "";

  const phone = String(
    options?.contact?.phonePrimary || options?.contact?.phone || "",
  ).trim();
  const phoneSecondary = String(options?.contact?.phoneSecondary || "").trim();
  const phonePart = phone ? `telefone/WhatsApp: ${phone}` : "";
  const secondaryContactPart = phoneSecondary
    ? `telefone secundário: ${phoneSecondary}`
    : "";
  const intimationPart =
    isAttorneyOfRecord && !suppressAddress && (addressPart || phone)
      ? "declarando receber citações e intimações no endereço profissional acima indicado" +
        (phone
          ? ", inclusive por meio do aplicativo WhatsApp no número de telefone informado"
          : "")
      : "";
  const _healthLabelMap = {
    DOENCA_GRAVE: "Doença Grave",
    DEFICIENCIA_FISICA: "Deficiência Física",
    DEFICIENCIA_MENTAL: "Deficiência Mental",
    AUTISMO: "TEA",
    IDOSO: "Idoso (60+)",
    SUPER_IDOSO: "Super Idoso (80+)",
  };
  const _healthArr = Array.isArray(options?.healthCondition)
    ? options.healthCondition.filter((c) => c && c !== "NENHUMA")
    : options?.healthCondition && options.healthCondition !== "NENHUMA"
      ? [String(options.healthCondition)]
      : [];
  const healthPart =
    _healthArr.length > 0
      ? `condição de saúde declarada: ${_healthArr.map((c) => _healthLabelMap[c] || c).join(", ")}`
      : "";
  const anchorRolePart = String(options?.anchorRoleLabel || "").trim()
    ? `vinculado(a) a ${String(options.anchorRoleLabel).trim()}`
    : "";
  const dependencyPart = options?.isFullDependent
    ? "em regime de dependência total de contato e residência do responsável"
    : "";

  const text = [
    ...baseParts,
    emailPart,
    addressPart,
    phonePart,
    secondaryContactPart,
    intimationPart,
    healthPart,
    anchorRolePart,
    dependencyPart,
  ]
    .filter(Boolean)
    .join(", ");
  const withPeriod = options?.withPeriod !== false;
  const finalized = withPeriod
    ? `${String(text)
        .trim()
        .replace(/[\s,.]+$/g, "")}.`
    : `${String(text)
        .trim()
        .replace(/[\s,.]+$/g, "")}`;

  // Sanitiza mojibakes que podem vir do Firestore
  const mojibakeFix = (s) => {
    const map = [
      ["Ã§", "ç"],
      ["Ã£", "ã"],
      ["Ã©", "é"],
      ["Ã¡", "á"],
      ["Ã¢", "â"],
      ["Ãª", "ê"],
      ["Ã³", "ó"],
      ["Ãµ", "õ"],
      ["Ã¼", "ü"],
      ["Ãº", "ú"],
      ["Ã­", "í"],
      ["Ã´", "ô"],
    ];
    let r = s;
    for (const [b, g] of map) r = r.replaceAll(b, g);
    return r;
  };
  return mojibakeFix(
    inflectInline(finalized, g)
      .replace(/nÂº\s*nÂº/gi, "n.")
      .replace(/n\.\s*nÂº/gi, "n."),
  );
};

const labeledValueOrMissing = (label, value, missingLabel, transform) => {
  const raw = String(value || "").trim();
  if (!raw) return `[${missingLabel}]`;
  const resolved = typeof transform === "function" ? transform(raw) : raw;
  return `${label} ${resolved}`;
};

const prefixedValueOrMissing = (prefix, value, missingLabel, transform) => {
  const raw = String(value || "").trim();
  if (!raw) return `[${missingLabel}]`;
  const resolved = typeof transform === "function" ? transform(raw) : raw;
  return `${prefix}${resolved}`;
};

const finalizeQualificationText = (parts = []) => {
  const text = (Array.isArray(parts) ? parts : [])
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .join(", ")
    .replace(/,\s*,/g, ", ")
    .replace(/[\s,.]+$/g, "")
    .trim();
  const withPeriod = parts.length > 0;
  const finalized = withPeriod
    ? `${String(text)
        .trim()
        .replace(/[\s,.]+$/g, "")}.`
    : `${String(text)
        .trim()
        .replace(/[\s,.]+$/g, "")}`;

  return finalized;
};

const renderAnimalText = (animalData, addressData, clientData = {}) => {
  const a = animalData || {};
  const tutor = a.responsible || a.guardian || {};
  const microchipValue = String(a.microchip || a.chipId || "").trim();
  const biologicalSexValue = String(a.biologicalSex || a.gender || "").trim();

  const bioSexLow = biologicalSexValue.toLowerCase();
  const animalG =
    bioSexLow === "fêmea" || bioSexLow === "femea" || bioSexLow === "f"
      ? "F"
      : "M";

  const tutorG = normGender(
    tutor.grammaticalGender || tutor.gender || "",
    tutor.pronoun || "",
    {
      nationality: tutor.nationality,
      civilStatus: tutor.civilStatus,
      profession: tutor.profession,
    },
  );
  const possessive = tutorG === "F" ? "sua" : "seu";

  const coat = a.coat || {};
  const coatParts = [
    String(coat?.color || "").trim() ? `cor ${String(coat.color).trim()}` : "",
    String(coat?.pattern || "").trim()
      ? `padronagem ${String(coat.pattern).trim()}`
      : "",
    String(coat?.texture || "").trim()
      ? `textura ${String(coat.texture).trim()}`
      : "",
  ].filter(Boolean);
  const distinctiveMarks = Array.isArray(a.distinctiveMarks)
    ? a.distinctiveMarks
        .map((item) => String(item || "").trim())
        .filter(Boolean)
    : String(a.distinctiveMarks || "").trim()
      ? [String(a.distinctiveMarks).trim()]
      : [];
  const registries = Array.isArray(a.registries)
    ? a.registries.map((item) => String(item || "").trim()).filter(Boolean)
    : String(a.registries || "").trim()
      ? [String(a.registries).trim()]
      : [];
  const residencyPart = clientData?.isFullDependent
    ? inflectInline(
        `residente e domiciliado(a) com ${possessive} Responsável Legal`,
        animalG,
      )
    : addressData?.street
      ? `residente em ${fmtAddr(addressData)}`
      : "";
  const _hMap2 = {
    DOENCA_GRAVE: "Doença Grave",
    DEFICIENCIA_FISICA: "Deficiência Física",
    DEFICIENCIA_MENTAL: "Deficiência Mental",
    AUTISMO: "TEA",
    IDOSO: "Idoso (60+)",
    SUPER_IDOSO: "Super Idoso (80+)",
  };
  const _hArr2 = Array.isArray(clientData?.healthCondition)
    ? clientData.healthCondition.filter((c) => c && c !== "NENHUMA")
    : clientData?.healthCondition && clientData.healthCondition !== "NENHUMA"
      ? [String(clientData.healthCondition)]
      : [];
  const healthPart =
    _hArr2.length > 0
      ? `condição de saúde declarada: ${_hArr2.map((c) => _hMap2[c] || c).join(", ")}`
      : "";
  const representedPart = inflectInline(
    `neste ato representado(a) por ${possessive} Responsável Legal`,
    animalG,
  );

  return finalizeQualificationText([
    strongUpper(a.name || "[Nome não informado]"),
    "animal não-humano (ser senciente e sujeito de direitos despersonificado)",
    prefixedValueOrMissing(
      "da espécie ",
      a.species,
      "Espécie não informada",
      lower,
    ),
    labeledValueOrMissing(
      "classificação científica",
      a.scientificName,
      "Classificação científica não informada",
    ),
    labeledValueOrMissing(
      "raça",
      a.breed,
      "Raça não informada",
      formatAnimalBreed,
    ),
    biologicalSexValue ? `sexo biológico ${lower(biologicalSexValue)}` : "",
    a.reproductiveStatus
      ? `estado reprodutivo ${lower(a.reproductiveStatus)}`
      : "",
    coatParts.length > 0 ? `pelagem ${coatParts.join(", ")}` : "",
    distinctiveMarks.length > 0
      ? `sinais distintivos: ${distinctiveMarks.join("; ")}`
      : "sem sinais característicos documentados",
    a.rga || microchipValue
      ? [
          a.rga ? `RGA n. ${String(a.rga).trim()}` : "",
          microchipValue ? `microchip n. ${microchipValue}` : "",
        ]
          .filter(Boolean)
          .join(", ")
      : "sem registro de RGA ou implante de microchip",
    registries.length > 0
      ? `registros complementares: ${registries.join("; ")}`
      : "",
    a.biological?.estimatedAge
      ? `idade estimada: ${String(a.biological.estimatedAge).trim()}`
      : "",
    a.originOfGuardianship
      ? `origem da guarda: ${lower(String(a.originOfGuardianship).trim())}`
      : "",
    a.health?.veterinarianName
      ? `veterinário responsável: ${String(a.health.veterinarianName).trim()}`
      : "",
    residencyPart,
    healthPart,
    representedPart,
    fmtRep(tutor),
  ]);
};

const renderMinorText = (
  minorData,
  addressData,
  clientData = {},
  rootGender = "",
) => {
  const m = minorData || {};
  const g = rootGender === "M" || rootGender === "F" ? rootGender : "M";
  const residencyPart = clientData?.isFullDependent
    ? inflectInline("residente e domiciliado(a) com seu Responsável Legal", g)
    : addressData?.street
      ? `residente em ${fmtAddr(addressData)}`
      : "";
  const _hMap3 = {
    DOENCA_GRAVE: "Doença Grave",
    DEFICIENCIA_FISICA: "Deficiência Física",
    DEFICIENCIA_MENTAL: "Deficiência Mental",
    AUTISMO: "TEA",
    IDOSO: "Idoso (60+)",
    SUPER_IDOSO: "Super Idoso (80+)",
  };
  const _hArr3 = Array.isArray(clientData?.healthCondition)
    ? clientData.healthCondition.filter((c) => c && c !== "NENHUMA")
    : clientData?.healthCondition && clientData.healthCondition !== "NENHUMA"
      ? [String(clientData.healthCondition)]
      : [];
  const healthPart =
    _hArr3.length > 0
      ? `condição de saúde declarada: ${_hArr3.map((c) => _hMap3[c] || c).join(", ")}`
      : "";

  const rawText = finalizeQualificationText([
    strongUpper(m.name || "[Nome não informado]"),
    lower(m.nationality || "[Nacionalidade não informada]"),
    lower(m.condition || "menor impúbere/púbere"),
    prefixedValueOrMissing(
      "portador(a) do Registro Geral (RG) n. ",
      fmtRgNum(m.rg),
      "RG não informado",
    ),
    prefixedValueOrMissing(
      "inscrito(a) no Cadastro de Pessoas Físicas (CPF) sob o n. ",
      fmtCpfNum(m.cpf),
      "CPF não informado",
    ),
    residencyPart,
    healthPart,
    String(m.representativeType || "").trim() &&
    String(m.representativeParent || "").trim()
      ? `neste ato ${m.representativeType} por ${m.representativeParent}`
      : String(m.representativeType || "").trim()
        ? `neste ato ${m.representativeType}`
        : String(m.representativeParent || "").trim()
          ? `neste ato por ${m.representativeParent}`
          : "[Qualidade de representação não informada]",
    m.guardianshipProcessNumber
      ? `nos termos do processo n. ${m.guardianshipProcessNumber}`
      : "",
    m.guardianshipCourt ? `tramitando perante ${m.guardianshipCourt}` : "",
    fmtRep(m.representative),
  ]);
  return inflectInline(rawText, g);
};

const renderImprisonedText = (imprisonedData, rootGender = "") => {
  const i = imprisonedData || {};
  const g = rootGender === "M" || rootGender === "F" ? rootGender : "M";
  const rgNum = fmtRgNum(i.rg);
  const cpfNum = fmtCpfNum(i.cpf);
  const unitParts = [
    String(i.prisonUnit || "").trim(),
    String(i.cityUnit || "").trim(),
  ]
    .filter(Boolean)
    .join(", ");

  const rawText = finalizeQualificationText([
    strongUpper(i.name || "[Nome não informado]"),
    lower(inflectInline(i.nationality || "[Nacionalidade não informada]", g)),
    lower(inflectInline(i.civilStatus || "de estado civil desconhecido", g)),
    lower(inflectInline(i.profession || "de profissão ignorada", g)),
    rgNum
      ? `portador(a) do Registro Geral (RG) n. ${rgNum}`
      : "portador(a) de Registro Geral não informado",
    cpfNum
      ? `inscrito(a) no Cadastro de Pessoas Físicas (CPF) sob o n. ${cpfNum}`
      : "CPF não informado ou desconhecido",
    unitParts
      ? `atualmente recolhido(a) em ${unitParts}`
      : "[Unidade prisional não informada]",
    i.matriculaPrisional
      ? `matrícula prisional n. ${i.matriculaPrisional}`
      : "com matrícula prisional do INFOPEN não informada",
    i.rji ? `RJI n. ${i.rji}` : "RJI não fornecido",
    i.executionRegime
      ? `regime de cumprimento: ${lower(i.executionRegime)}`
      : "",
    i.penalExecutionCourt
      ? `Vara de Execução Penal: ${i.penalExecutionCourt}`
      : "",
    String(i.proxyRelation || "").trim()
      ? `neste ato representado(a) por seu(sua) ${i.proxyRelation}`
      : "[Representante não informado]",
    fmtRep(i.proxy),
  ]);
  return inflectInline(rawText, g);
};

const renderEstateText = (estateData) => {
  const e = estateData || {};
  const cpfNum = fmtCpfNum(e.cpf);
  return finalizeQualificationText([
    `O <strong>ESPÓLIO DE ${escapeHtml(String(e.deceasedName || e.name || "[Falecido não informado]").toUpperCase())}</strong>`,
    lower(e.nationality || "[Nacionalidade não informada]"),
    lower(e.profession || "de profissão ignorada"),
    cpfNum
      ? `inscrito no Cadastro de Pessoas Físicas (CPF) sob o n. ${cpfNum}`
      : "CPF não informado ou desconhecido",
    e.dateOfDeath
      ? `falecido em ${fmtDate(e.dateOfDeath)}`
      : "[Data do óbito não informada]",
    "neste ato representado por seu(sua) inventariante",
    fmtRep(e.inventorier || e.representative || e.inventor || {}),
  ]);
};

const renderCondominiumText = (condominiumData, addressData) => {
  const c = condominiumData || {};
  return finalizeQualificationText([
    strongUpper(c.name || "[Condomínio não informado]"),
    "ente despersonalizado",
    fmtCnpj(c.cnpj)
      ? `inscrito no CNPJ ${fmtCnpj(c.cnpj)}`
      : "[CNPJ não informado]",
    `com endereço na ${fmtAddr(addressData || {})}`,
    "neste ato representado por seu(sua) síndico(a)",
    fmtRep(c.syndic),
    c.syndicElectionDate
      ? `eleito(a) em assembleia de ${fmtDate(c.syndicElectionDate)}`
      : "",
    c.syndicMandateExpiration
      ? `com mandato vigente até ${fmtDate(c.syndicMandateExpiration)}`
      : "",
    "conforme Ata de Eleição anexa",
  ]);
};

const renderUnbornText = (unbornData) => {
  const u = unbornData || {};
  return finalizeQualificationText([
    "O <strong>NASCITURO</strong>",
    String(u.mothersName || "").trim()
      ? `sob a representação de sua gestante ${strongUpper(u.mothersName)}`
      : "[Gestante não informada]",
    u.conceptionDate
      ? `com concepção aproximada em ${u.conceptionDate}`
      : "com data aproximada de concepção ignorada",
    u.expectedBirthDate
      ? `e data provável do parto em ${u.expectedBirthDate}`
      : "",
    u.gestation?.ultrasoundDate
      ? `confirmada por ultrassonografia de ${fmtDate(u.gestation.ultrasoundDate)}`
      : "",
    u.gestation?.medicalProfessional
      ? `atestada pelo(a) Dr(a). ${u.gestation.medicalProfessional}`
      : "",
    "neste ato representado(a) por",
    fmtRep(u.representative),
  ]);
};

const renderBankruptcyText = (bankruptcyData) => {
  const b = bankruptcyData || {};
  return finalizeQualificationText([
    `A <strong>MASSA FALIDA DE ${escapeHtml(String(b.companyName || "[Empresa não informada]").toUpperCase())}</strong>`,
    fmtCnpj(b.cnpj)
      ? `inscrita no CNPJ ${fmtCnpj(b.cnpj)}`
      : "[CNPJ não informado]",
    b.decreeDate ? `com falência decretada em ${fmtDate(b.decreeDate)}` : "",
    b.universalCourt ? `perante o ${b.universalCourt}` : "",
    "neste ato representada por seu Administrador Judicial",
    fmtRep(b.administrator),
  ]);
};

const renderLegalEntityText = (legalData, addressData) => {
  const l = legalData || {};
  const addr = addressData || {};
  const rep = l.representative || {};
  const cnpjFormatted = fmtCnpj(l.cnpj);
  const stateRegistryPart = l.stateRegistry
    ? `Inscrição Estadual n. ${l.stateRegistry}`
    : "";
  const nirePart = l.nire ? `NIRE n. ${l.nire}` : "";
  const constitutionPart = l.constitutionDate
    ? `constituída em ${fmtDate(l.constitutionDate)}`
    : "";
  const companyTypePart = l.companyType
    ? lower(l.companyType)
    : "pessoa jurídica de direito privado";
  const role = String(l.representativeRole || "Sócio-Administrador").trim();

  return finalizeQualificationText([
    strongUpper(l.companyName || "[Razão social não informada]"),
    l.tradeName ? `(nome fantasia: ${l.tradeName})` : "",
    companyTypePart,
    cnpjFormatted
      ? `inscrita no CNPJ ${cnpjFormatted}`
      : "CNPJ não informado ou desconhecido",
    stateRegistryPart,
    nirePart,
    constitutionPart,
    addr.street ? `com sede na ${fmtAddr(addr)}` : "",
    `neste ato representada por seu(sua) ${role}`,
    fmtRep(rep),
  ]);
};

const fmtRep = (representative) => {
  const rep = representative || {};
  return renderPhysicalPersonText(
    {
      name: String(rep?.name || "").trim(),
      socialName: String(rep?.socialName || "").trim(),
      pronoun: String(rep?.pronoun || "").trim(),
      grammaticalGender: String(
        rep?.grammaticalGender || rep?.gender || "",
      ).trim(),
      birthDate: String(rep?.birthDate || "").trim(),
      approximateAgeMonths: String(rep?.approximateAgeMonths || "").trim(),
      nationality: String(rep?.nationality || "").trim(),
      naturalness: String(rep?.naturalness || "").trim(),
      civilStatus: String(rep?.civilStatus || "").trim(),
      maritalRegime: String(rep?.maritalRegime || "").trim(),
      spouseName: String(rep?.spouseName || "").trim(),
      spouseCPF: String(rep?.spouseCPF || "").trim(),
      profession: String(rep?.profession || "").trim(),
      rg: String(rep?.rg || "").trim(),
      orgaoEmissor: String(rep?.orgaoEmissor || "").trim(),
      cpf: String(rep?.cpf || "").trim(),
      hasProfessionalRegister: Boolean(rep?.hasProfessionalRegister),
      registerType: String(rep?.registerType || "").trim(),
      registerUf: String(rep?.registerUf || "").trim(),
      professionalRegister: String(rep?.professionalRegister || "").trim(),
    },
    rep?.address || {},
    {
      contact: rep?.contact || {},
      suppressContact: false,
      withPeriod: false,
    },
  );
};

export const buildSignatureLine = (clientData) => {
  const client = clientData || {};
  const q = client?.qualification || {};
  const type = String(client?.type || CLIENT_TYPES.PHYSICAL).trim();

  const fmtCpfLabel = (value) => {
    const cpf = fmtCpfNum(value);
    return cpf ? `CPF: ${cpf}` : "CPF: [Não informado]";
  };

  const buildLines = (name, cpf) => {
    const safeName = String(name || "").trim() || "[Signatário não informado]";
    return {
      nameLine: safeName,
      cpfLine: fmtCpfLabel(cpf),
    };
  };

  // `representedEntityName`/`representedEntityType` (adicionados 27/08/2026,
  // Art. 395, CONSTITUICAO_REGRAS.SH): campos ADITIVOS — nenhum consumidor
  // existente quebra, pois ninguém além do novo código lia essas chaves.
  // Permitem que um documento com múltiplas "partes" que compartilham o
  // MESMO signatário real (ex.: 1 tutor(a) representando 2 animais
  // co-clientes) monte 1 bloco de assinatura combinado em vez de repetir o
  // nome/CPF do signatário em blocos separados — ver TermoRevogacaoCliente
  // Engine.jsx `renderSignature`.
  if (type === CLIENT_TYPES.LEGAL) {
    const companyName =
      String(q?.legal?.companyName || client?.name || "").trim() ||
      "[Pessoa Jurídica não informada]";
    const rep = q?.legal?.representative || {};
    return {
      ...buildLines(rep?.name, rep?.cpf),
      subtitle: `(Assinando na qualidade de representante legal de ${companyName})`,
      representedEntityName: companyName,
      representedEntityType: type,
    };
  }

  if (type === CLIENT_TYPES.ANIMAL) {
    const animalName =
      String(q?.animal?.name || client?.name || "").trim() ||
      "[Animal não informado]";
    const rep = q?.animal?.responsible || q?.animal?.guardian || {};
    return {
      ...buildLines(rep?.name, rep?.cpf),
      subtitle: `(Assinando na qualidade de responsável legal pelo animal não-humano ${animalName})`,
      representedEntityName: animalName,
      representedEntityType: type,
    };
  }

  if (type === CLIENT_TYPES.MINOR) {
    const minorName =
      String(q?.minor?.name || client?.name || "").trim() ||
      "[Menor não informado]";
    const rep = q?.minor?.representative || {};
    return {
      ...buildLines(rep?.name, rep?.cpf),
      subtitle: `(Assinando na qualidade de representante legal de ${minorName})`,
      representedEntityName: minorName,
      representedEntityType: type,
    };
  }

  if (type === CLIENT_TYPES.ESTATE) {
    const deceasedName =
      String(
        q?.estate?.deceasedName || q?.estate?.name || client?.name || "",
      ).trim() || "[Falecido não informado]";
    const rep =
      q?.estate?.inventorier ||
      q?.estate?.representative ||
      q?.estate?.inventor ||
      {};
    return {
      ...buildLines(rep?.name, rep?.cpf),
      subtitle: `(Assinando na qualidade de inventariante do Espólio de ${deceasedName})`,
      representedEntityName: deceasedName,
      representedEntityType: type,
    };
  }

  const physical = q?.physical || {};
  return {
    ...buildLines(
      physical?.socialName || physical?.name || client?.name,
      physical?.cpf || client?.cpf,
    ),
    subtitle: "",
  };
};

export const generateQualificationText = (clientData, options = {}) => {
  if (!clientData) return "[Dados do cliente não encontrados]";

  const q = clientData.qualification || {};
  const l = clientData.location || {};
  const type = clientData.type;
  const anchorRoleLabel = String(clientData?.anchor?.role || "").trim();
  const rootGender = String(clientData?.grammaticalGender || "")
    .trim()
    .toUpperCase();

  if (type === CLIENT_TYPES.PHYSICAL) {
    const physData = { ...(q.physical || {}) };
    if (rootGender === "M" || rootGender === "F")
      physData.grammaticalGender = rootGender;
    return renderPhysicalPersonText(physData, l?.address || {}, {
      locationStatus: l?.status,
      contact: clientData?.contact || {},
      healthCondition: clientData?.healthCondition || "",
      isFullDependent: Boolean(clientData?.isFullDependent),
      anchorRoleLabel,
      suppressContact: false,
      withPeriod: true,
      isAttorneyOfRecord: Boolean(options?.isAttorneyOfRecord),
    });
  }

  if (type === CLIENT_TYPES.ANIMAL) {
    return renderAnimalText(q.animal, l?.address || {}, clientData);
  }

  if (type === CLIENT_TYPES.MINOR) {
    return renderMinorText(q.minor, l?.address || {}, clientData, rootGender);
  }

  if (type === CLIENT_TYPES.IMPRISONED) {
    return renderImprisonedText(q.imprisoned, rootGender);
  }

  if (type === CLIENT_TYPES.LEGAL) {
    return renderLegalEntityText(q.legal, l?.address || {});
  }

  if (type === CLIENT_TYPES.ESTATE) {
    return renderEstateText(q.estate);
  }

  if (type === CLIENT_TYPES.CONDOMINIUM) {
    return renderCondominiumText(q.condominium, l?.address || {});
  }

  if (type === CLIENT_TYPES.UNBORN || type === CLIENT_TYPES.NASCITURO) {
    return renderUnbornText(q.unborn);
  }

  if (type === CLIENT_TYPES.BANKRUPTCY) {
    return renderBankruptcyText(q.bankruptcy);
  }

  return "[Qualificação não gerada]";
};
